import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  DeleteBeneficiariesMutation,
  DeleteBeneficiariesMutationVariables,
  GetBeneficiariesQuery,
  GetBeneficiariesQueryVariables,
  UpdateBeneficiariesMutation,
  UpdateBeneficiariesMutationVariables,
} from "types/graphql";

export function getAllBeneficiaries(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetBeneficiariesQuery["getBeneficiaries"]> {
  return new Promise((resolve, reject) => {
    const BENEFICIARIES = gql`
      query getBeneficiaries($accountId: ID!) {
        getBeneficiaries(accountId: $accountId) {
          familyName
          givenName
          city
          country
          postalCode
          state
          dateOfBirth
          socialSecurityNumber
          relationship
          beneficiaryType
          sharePercent
          streetAddress
        }
      }
    `;
    const result = client.query<
      GetBeneficiariesQuery,
      GetBeneficiariesQueryVariables
    >({
      query: BENEFICIARIES,
      variables: {
        accountId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getBeneficiaries);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function updateBeneficiaries(
  client: ApolloClient<object>,
  beneficiaryInput: UpdateBeneficiariesMutationVariables["beneficiaryInput"]
): Promise<UpdateBeneficiariesMutation["updateBeneficiaries"]> {
  return new Promise((resolve, reject) => {
    const UPDATE_BENEFICIARIES = gql`
      mutation updateBeneficiaries($beneficiaryInput: BeneficiaryInput!) {
        updateBeneficiaries(beneficiaryInput: $beneficiaryInput) {
          familyName
          givenName
          city
          country
          postalCode
          state
          dateOfBirth
          socialSecurityNumber
          relationship
          beneficiaryType
          sharePercent
          streetAddress
        }
      }
    `;
    const result = client.mutate<
      UpdateBeneficiariesMutation,
      UpdateBeneficiariesMutationVariables
    >({
      mutation: UPDATE_BENEFICIARIES,
      variables: {
        beneficiaryInput,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.updateBeneficiaries ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function deleteAllBeneficiaries(
  client: ApolloClient<object>,
  accountId: string
): Promise<DeleteBeneficiariesMutation["deleteBeneficiaries"]> {
  return new Promise((resolve, reject) => {
    const DELETE_BENEFICIARIES = gql`
      mutation deleteBeneficiaries($accountId: ID!) {
        deleteBeneficiaries(accountId: $accountId)
      }
    `;
    const result = client.mutate<
      DeleteBeneficiariesMutation,
      DeleteBeneficiariesMutationVariables
    >({
      mutation: DELETE_BENEFICIARIES,
      variables: { accountId },
    });
    result.then(
      (data) => {
        resolve(data.data?.deleteBeneficiaries ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export default {
  getAllBeneficiaries,
  updateBeneficiaries,
  deleteAllBeneficiaries,
};
